import { AppUrls } from "../../ts/app";
import { Logger } from "../../ts/utils/logger";
import { DialogController } from "../dialog-controller";

export class LoginDialogController extends DialogController<null> {
    private passwordsVisible: boolean = false;
    private loading: boolean = false;
    private errors: string[] = [];

    constructor() {
        super({
            logLabel: 'LoginDialogController',
            dialogId: 'login-dialog',
            closeButtonId: 'login-dialog-close-button',
        });
    }

    public initialize(): void {
        super.initialize();
        this.bindClick({
            elementId: "toggle-password-visibility",
            handler: () => {
                this.passwordsVisible = !this.passwordsVisible;
                this._setState();
            },
        });

        this.bindClick({
            elementId: "login-submit-button",
            handler: this._submitLogin.bind(this),
        });

        this._setupFieldListeners();
        this._setState();
        Logger.info(this.logLabel, "Initialized");
    }

    protected onDialogOpened(): void {
        setTimeout(() => {
            const usernameField = this.getElementByIdOrWarn<HTMLInputElement>("username");
            usernameField?.focus();
        }, 0);
    }

    protected onDialogClosed(): void {
        this._clearForm();
        this._setState();
    }


    private _setState(): void {
        this._setErrorMessagesState();
        this._setPasswordVisibilityState();
        this._setLoginButtonState();
    }

    private _setErrorMessagesState(): void {
        const loginFormErrorMessage = this.getElementByIdOrWarn("login-form-error-message");
        if (loginFormErrorMessage) {
            loginFormErrorMessage.style.visibility = this.errors.length > 0 ? "visible" : "hidden";
            loginFormErrorMessage.textContent = this.errors.join(" ");
            loginFormErrorMessage.setAttribute("aria-hidden", this.errors.length === 0 ? "true" : "false");
        }
    }

    private _setPasswordVisibilityState(): void {
        const passwordField = this.getElementByIdOrWarn<HTMLInputElement>("password");
        const togglePasswordVisibilityButton = this.getElementByIdOrWarn<HTMLButtonElement>("toggle-password-visibility");

        if (passwordField && togglePasswordVisibilityButton) {
            const type = this.passwordsVisible ? "text" : "password";
            passwordField.type = type;
            togglePasswordVisibilityButton.textContent = this.passwordsVisible ? "Hide" : "Show";
            togglePasswordVisibilityButton.setAttribute(
                "aria-label",
                this.passwordsVisible ? "Hide password" : "Show password"
            );
        }
    }

    private _setLoginButtonState(): void {
        const loginButton = this.getElementByIdOrWarn<HTMLButtonElement>("login-submit-button");
        const loginButtonText = this.getElementByIdOrWarn("login-submit-button-text");
        const loginSpinner = this.getElementByIdOrWarn("login-submit-spinner");

        if (loginButton && loginButtonText && loginSpinner) {
            loginButton.disabled = this.loading;
            loginButton.style.opacity = this.loading ? "0.6" : "1";
            loginButton.style.cursor = this.loading ? "not-allowed" : "pointer";
            loginButtonText.style.display = this.loading ? "none" : "inline";
            loginSpinner.style.display = this.loading ? "block" : "none";
        }
    }

    private _setupFieldListeners(): void {
        const usernameField = this.getElementByIdOrWarn<HTMLInputElement>("username");
        const passwordField = this.getElementByIdOrWarn<HTMLInputElement>("password");

        if (usernameField) {
            usernameField.addEventListener("keydown", (event) => {
                if (event.key === "Enter") {
                    event.preventDefault();
                    passwordField?.focus();
                }
            });
        }

        if (passwordField) {
            passwordField.addEventListener("keydown", (event) => {
                if (event.key === "Enter") {
                    event.preventDefault();
                    this._submitLogin();
                }
            });
        }
    }

    private async _submitLogin(): Promise<void> {
        this.errors = [];
        this._setState();

        const username = this.getElementByIdOrWarn<HTMLInputElement>("username")?.value.trim();
        const password = this.getElementByIdOrWarn<HTMLInputElement>("password")?.value.trim();

        const valid = this._validateForm({ username, password });
        if (!valid) {
            this._setState();
            return;
        }

        this.loading = true;
        this._setState();

        try {
            const response = await fetch(AppUrls.login || "", {
                method: "POST",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ username, password }),
            });

            if (response.ok) {
                location.reload();
            } else {
                const data = await response.json();
                this.errors = this.parseErrors(data);
                this.loading = false;
                this._setState();
            }
        } catch (error) {
            Logger.error(this.logLabel, `Login request failed: ${error}`);
            this.errors = ["An error occurred. Please try again."];
            this.loading = false;
            this._setState();
        }
    }

    private _clearForm(): void {
        const username = this.getElementByIdOrWarn<HTMLInputElement>("username");
        const password = this.getElementByIdOrWarn<HTMLInputElement>("password");

        if (username) {
            username.value = "";
        }
        if (password) {
            password.value = "";
        }
    }

    private _validateForm({
        username,
        password,
    }: {
        username: string;
        password: string
    }): boolean {
        let valid = true;

        const usernameError = this.getElementByIdOrWarn("login-username-error");
        const passwordError = this.getElementByIdOrWarn("login-password-error");

        if (usernameError) {
            if (!username) {
                usernameError.textContent = "Required";
                usernameError.setAttribute("aria-hidden", "false");
                valid = false;
            } else {
                usernameError.textContent = "";
                usernameError.setAttribute("aria-hidden", "true");
            }
        }

        if (passwordError) {
            if (!password) {
                passwordError.textContent = "Required";
                passwordError.setAttribute("aria-hidden", "false");
                valid = false;
            } else {
                passwordError.textContent = "";
                passwordError.setAttribute("aria-hidden", "true");
            }
        }

        return valid;
    }
}
